import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowsClockwise, CheckCircle, XCircle, Warning, FolderOpen, Trash, X,
} from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN") : "-";

export default function SyncLog() {
  const [logs, setLogs] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const [importFiles, setImportFiles] = useState({ processed: [], failed: [] });

  const [selected, setSelected] = useState(new Set());
  const [singleDeleteTarget, setSingleDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/sync/logs", { params: { limit: 100 } });
      setLogs(data);
      // Drop any selected ids that no longer exist in the freshly loaded list.
      setSelected((prev) => {
        const ids = new Set(data.map((l) => l.id));
        return new Set(Array.from(prev).filter((id) => ids.has(id)));
      });
    }
    catch (e) { toast.error(formatErr(e)); }
  };

  const loadImportFiles = async () => {
    try { const { data } = await api.get("/tally-import/files", { params: { limit: 10 } }); setImportFiles(data); }
    catch (e) { /* non-critical — table below already shows every run */ }
  };

  useEffect(() => { load(); loadImportFiles(); }, []);

  const doSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/now");
      if (data.status === "success") toast.success(`Added ${data.added} • Skipped ${data.duplicates}`);
      else if (data.status === "partial") toast.info("Partial sync — 0 records");
      else toast.error(data.errors?.[0] || "Sync failed");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSyncing(false); }
  };

  const allSelected = logs.length > 0 && logs.every((l) => selected.has(l.id));
  const someSelected = logs.some((l) => selected.has(l.id));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      logs.forEach((l) => next.add(l.id));
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doSingleDelete = async () => {
    if (!singleDeleteTarget) return;
    setBusy(true);
    try {
      await api.delete(`/sync/logs/${encodeURIComponent(singleDeleteTarget.id)}`);
      const n = singleDeleteTarget.added || 0;
      toast.success(n > 0 ? `Sync log deleted — ${n} sale${n === 1 ? "" : "s"} removed too` : "Sync log deleted");
      setSingleDeleteTarget(null);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(singleDeleteTarget.id);
        return next;
      });
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doBulkDelete = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const log_ids = Array.from(selected);
      const { data } = await api.post("/sync/logs/bulk-delete", { log_ids });
      const n = data.sales_deleted || 0;
      toast.success(`Deleted ${data.deleted} sync log${data.deleted === 1 ? "" : "s"}` + (n > 0 ? ` — ${n} sale${n === 1 ? "" : "s"} removed too` : ""));
      setBulkDeleteOpen(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const badge = (s) => {
    const map = {
      success: { c: "border-signal/40 bg-signal/10 text-signal dark:text-signal", i: CheckCircle },
      partial: { c: "border-signal/40 bg-signal/10 text-signal dark:text-signal", i: Warning },
      fail:    { c: "border-signal/40 bg-signal/10 text-signal dark:text-signal", i: XCircle },
    };
    const m = map[s] || map.partial;
    const I = m.i;
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border ${m.c}`}><I size={12} weight="fill" /> {s}</span>;
  };

  return (
    <div className="space-y-6" data-testid="sync-log-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Sync Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Automatic Tally sync history (via configured Tally Server URL).</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
          <button onClick={doSync} disabled={syncing} data-testid="sync-now-btn-log"
            className="signal-btn h-10 px-4 rounded-md text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60">
            <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin-slow" : ""} />
            {syncing ? "Syncing..." : "Sync Now"}
          </button>
        </div>
      </div>

      {(importFiles.processed.length > 0 || importFiles.failed.length > 0) && (
        <div className="brut-card p-5 space-y-4" data-testid="tally-upload-card">
          {(importFiles.processed.length > 0 || importFiles.failed.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  <FolderOpen size={14} /> Recent processed/
                </div>
                <ul className="space-y-1 text-xs font-mono max-h-32 overflow-y-auto">
                  {importFiles.processed.length === 0 && <li className="text-muted-foreground">— empty —</li>}
                  {importFiles.processed.map((f) => <li key={f.name} className="truncate text-signal dark:text-signal">{f.name}</li>)}
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  <FolderOpen size={14} /> Recent failed/
                </div>
                <ul className="space-y-1 text-xs font-mono max-h-32 overflow-y-auto">
                  {importFiles.failed.length === 0 && <li className="text-muted-foreground">— empty —</li>}
                  {importFiles.failed.map((f) => <li key={f.name} className="truncate text-signal dark:text-signal">{f.name}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="brut-card">
        <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-xs uppercase tracking-[0.05em] text-muted-foreground">
              <th className="text-left px-4 py-3 font-semibold w-10">
                <Checkbox
                  checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleAll}
                  disabled={logs.length === 0}
                  aria-label="Select all sync logs"
                  data-testid="sync-log-select-all-checkbox"
                />
              </th>
              <th className="text-left px-4 py-3">Started</th>
              <th className="text-left px-4 py-3">Trigger</th>
              <th className="text-left px-4 py-3">Mode</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Added</th>
              <th className="text-right px-4 py-3">Updated</th>
              <th className="text-right px-4 py-3">Duplicates</th>
              <th className="text-left px-4 py-3">Errors</th>
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody data-testid="sync-logs-body">
            {logs.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-muted-foreground">No sync runs yet</td></tr>}
            {logs.map((l) => (
              <tr
                key={l.id}
                className={`border-t border-border align-top ${selected.has(l.id) ? "bg-signal/5" : ""}`}
                data-testid={`sync-log-row-${l.id}`}
              >
                <td className="px-4 py-3">
                  <Checkbox
                    checked={selected.has(l.id)}
                    onCheckedChange={() => toggleOne(l.id)}
                    aria-label={`Select sync log from ${fmt(l.started_at)}`}
                    data-testid={`sync-log-row-checkbox-${l.id}`}
                  />
                </td>
                <td className="px-4 py-3 font-mono text-xs">{fmt(l.started_at)}</td>
                <td className="px-4 py-3 font-mono text-xs">{l.triggered_by}</td>
                <td className="px-4 py-3"><span className="font-mono uppercase text-xs">{l.mode}</span></td>
                <td className="px-4 py-3">{badge(l.status)}</td>
                <td className="px-4 py-3 text-right font-mono">{l.added}</td>
                <td className="px-4 py-3 text-right font-mono">{l.updated ?? 0}</td>
                <td className="px-4 py-3 text-right font-mono">{l.duplicates}</td>
                <td className="px-4 py-3 font-mono text-xs text-signal dark:text-signal max-w-md truncate">
                  {(l.errors || []).join("; ")}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setSingleDeleteTarget(l)}
                    data-testid={`sync-log-delete-btn-${l.id}`}
                    aria-label="Delete sync log"
                    title="Delete"
                    className="h-8 w-8 rounded-md inline-flex items-center justify-center border border-signal/40 bg-signal/10 text-signal hover:bg-signal/20 transition-colors"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Mobile card list — shown below md breakpoint instead of the table */}
        <div className="md:hidden divide-y divide-border">
          {logs.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">No sync runs yet</div>
          )}
          {logs.map((l) => (
            <div
              key={l.id}
              data-testid={`sync-log-card-${l.id}`}
              className={`p-4 space-y-3 ${selected.has(l.id) ? "bg-signal/5" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="pt-0.5">
                    <Checkbox
                      checked={selected.has(l.id)}
                      onCheckedChange={() => toggleOne(l.id)}
                      aria-label={`Select sync log from ${fmt(l.started_at)}`}
                      data-testid={`sync-log-row-checkbox-mobile-${l.id}`}
                    />
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-muted-foreground">{fmt(l.started_at)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{l.triggered_by} • <span className="uppercase">{l.mode}</span></div>
                  </div>
                </div>
                {badge(l.status)}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="font-mono">Added: {l.added}</span>
                <span className="font-mono">Updated: {l.updated ?? 0}</span>
                <span className="font-mono">Duplicates: {l.duplicates}</span>
              </div>

              {(l.errors || []).length > 0 && (
                <div className="text-xs font-mono text-signal dark:text-signal break-words">
                  {(l.errors || []).join("; ")}
                </div>
              )}

              <div>
                <button
                  onClick={() => setSingleDeleteTarget(l)}
                  data-testid={`sync-log-delete-btn-mobile-${l.id}`}
                  className="h-9 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5 border border-signal/40 bg-signal/10 text-signal"
                >
                  <Trash size={14} weight="bold" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating bulk action bar — appears once 1+ rows are selected */}
      {selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 px-5 py-3 rounded-md border border-border bg-card shadow-lg max-w-[calc(100vw-2rem)] flex-wrap justify-center"
          data-testid="sync-log-bulk-action-bar"
        >
          <span className="text-sm font-medium">
            {selected.size} item{selected.size === 1 ? "" : "s"} selected
          </span>
          <button
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="sync-log-bulk-delete-button"
            className="h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
          >
            <Trash size={15} weight="bold" /> Delete Selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            data-testid="sync-log-bulk-clear-button"
            aria-label="Clear selection"
            className="h-9 w-9 rounded-md border border-border inline-flex items-center justify-center hover:bg-muted"
          >
            <X size={15} />
          </button>
        </div>
      )}

      {/* Single delete confirmation */}
      <AlertDialog open={!!singleDeleteTarget} onOpenChange={(o) => !o && setSingleDeleteTarget(null)}>
        <AlertDialogContent data-testid="sync-log-single-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sync Log</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sync log entry from <strong>{fmt(singleDeleteTarget?.started_at)}</strong>?
              This will also permanently delete every sale that this import added ({singleDeleteTarget?.added ?? 0} sale{(singleDeleteTarget?.added ?? 0) === 1 ? "" : "s"}) from Dashboard / Pending / Delivered Orders. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sync-log-single-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doSingleDelete}
              disabled={busy}
              data-testid="sync-log-single-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? "Deleting..." : "Confirm Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={(o) => !o && setBulkDeleteOpen(false)}>
        <AlertDialogContent data-testid="sync-log-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Sync Logs</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selected.size}</strong> selected sync log{selected.size === 1 ? "" : "s"}?
              This will also permanently delete every sale those imports added from Dashboard / Pending / Delivered Orders. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sync-log-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doBulkDelete}
              disabled={busy}
              data-testid="sync-log-bulk-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? "Deleting..." : "Confirm Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
